import { createSelectListCollection } from '../../../components/select';
import type {
  DnsZoneSurfaceAttachBodyDto,
  DnsZoneSurfaceAttachmentDto,
  DnsZoneSurfaceDetachBodyDto,
  DnsZoneSurfaceProviderBodyDto,
  UpdateDnsZonePolicyBodyDto,
} from '@terra/shared-contracts-external-network';

export type DnsZoneAutoScope = NonNullable<
  UpdateDnsZonePolicyBodyDto['autoScope']
>;

export const DNS_ZONE_AUTO_SCOPE_OPTIONS: DnsZoneAutoScope[] = [
  'IN_SCOPE',
  'OUT_OF_SCOPE',
];

export const STATUS_COLOR: Record<
  NonNullable<DnsZoneSurfaceAttachmentDto['status']>,
  string
> = {
  ACTIVE: 'green',
  SYNC_ERROR: 'red',
  DISCONNECTED: 'gray',
};

export const DELEGATION_COLOR: Record<
  NonNullable<DnsZoneSurfaceAttachmentDto['delegationStatus']>,
  string
> = {
  VERIFIED: 'green',
  PENDING_VERIFICATION: 'yellow',
  UNVERIFIABLE: 'gray',
  MISMATCH: 'red',
};

export const attachmentKey = (
  attachment: DnsZoneSurfaceAttachmentDto,
): string =>
  attachment.zoneId ??
  `${attachment.provider}:${attachment.zoneRef ?? attachment.connectionId ?? 'pending'}`;

export const toSyncBody = (
  attachment: DnsZoneSurfaceAttachmentDto,
): DnsZoneSurfaceProviderBodyDto => ({
  provider: attachment.provider,
  ...(attachment.zoneId ? { zoneId: attachment.zoneId } : {}),
  ...(attachment.connectionId ? { connectionId: attachment.connectionId } : {}),
});

export const toAttachBody = (
  attachment: DnsZoneSurfaceAttachmentDto,
  autoScope: DnsZoneAutoScope = 'IN_SCOPE',
): DnsZoneSurfaceAttachBodyDto | null => {
  if (!attachment.zoneId) {
    return null;
  }

  return {
    provider: attachment.provider,
    zoneId: attachment.zoneId,
    autoScope,
    ...(attachment.connectionId
      ? { connectionId: attachment.connectionId }
      : {}),
  };
};

export const toDetachBody = (
  attachment: DnsZoneSurfaceAttachmentDto,
): DnsZoneSurfaceDetachBodyDto | null => {
  if (!attachment.zoneId) {
    return null;
  }

  return {
    provider: attachment.provider,
    zoneId: attachment.zoneId,
    ...(attachment.connectionId
      ? { connectionId: attachment.connectionId }
      : {}),
  };
};

export const canAttachDnsZone = (
  attachment: DnsZoneSurfaceAttachmentDto,
): boolean =>
  Boolean(attachment.zoneId) &&
  !attachment.attached &&
  !attachment.isPrivate &&
  attachment.delegationStatus !== 'MISMATCH';

export const shouldShowDnsZoneRecheck = (
  attachment: DnsZoneSurfaceAttachmentDto,
): boolean =>
  Boolean(attachment.zoneId) &&
  (attachment.attached ||
    (attachment.delegationStatus != null &&
      attachment.delegationStatus !== 'VERIFIED'));

export const resolveAutoScope = (
  autoScope: DnsZoneSurfaceAttachmentDto['autoScope'],
): DnsZoneAutoScope =>
  DNS_ZONE_AUTO_SCOPE_OPTIONS.find((option) => option === autoScope) ??
  'IN_SCOPE';

export const createDnsZoneAutoScopeCollection = ({
  labels,
}: {
  labels: Record<DnsZoneAutoScope, string>;
}) =>
  createSelectListCollection({
    items: DNS_ZONE_AUTO_SCOPE_OPTIONS.map((option) => ({
      value: option,
      label: labels[option],
    })),
  });

export type DnsZoneAutoScopeCollection = ReturnType<
  typeof createDnsZoneAutoScopeCollection
>;
